import React from 'react';
import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { numericSort } from 'utilities/sort';
import { generateURL } from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import type { DropdownOption } from 'types/common/DropdownOption';
import type {
  UiComponentResponse,
  UiComponent,
} from 'types/SparkFormBuilder/UiComponents';

const getUiComponents = () => {
  const url = generateURL(SPARK_RESOURCE_TYPES.UI_COMPONENTS);
  return fetchWrapper<UiComponentResponse>({
    url: url,
    method: 'GET',
  });
};

export function useUiComponents() {
  const queryResult = useQuery('uiComponents', getUiComponents, {
    onError: (error) => {
      if (error instanceof Error) {
        toastr.error(`Failed to get UI Components: ${error.message}`);
      }
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const { data } = queryResult;
  const { data: uiComponents } = data || {};

  const sortedUiComponents = uiComponents?.sort((a, b) => {
    return numericSort(a.attributes.sortOrder, b.attributes.sortOrder);
  });

  const dropdownOptions: DropdownOption[] =
    React.useMemo(
      () =>
        sortedUiComponents?.map((component) => ({
          label: component.attributes.name,
          value: component.id,
          iconUrl: component.attributes.iconURL,
        })),
      [sortedUiComponents]
    ) || [];

  const idMap = React.useMemo(() => {
    return sortedUiComponents?.reduce<Record<string, UiComponent>>(
      (acc, curr) => {
        return { ...acc, [curr.id]: curr };
      },
      {}
    );
  }, [sortedUiComponents]);

  const uiComponentsIdByNameMap = React.useMemo(() => {
    return sortedUiComponents?.reduce<Record<string, string>>((acc, curr) => {
      return { ...acc, [curr.attributes.name]: curr.id };
    }, {});
  }, [sortedUiComponents]);

  return {
    dropdownOptions,
    uiComponentsIdMap: idMap,
    uiComponents: sortedUiComponents,
    uiComponentsIdByNameMap,
    ...queryResult,
  };
}
